/** @jsx jsx */
import { jsx, css } from '@emotion/react';
import { FC, Fragment, useEffect, useMemo } from 'react';
import { Form, Formik, FormikProps } from 'formik';
import { useTranslation } from 'react-i18next';
import Button from '@airwallex/button';
import Flags from '@airwallex/flags';
import { formatCurrency, currencyNames, space } from '@airwallex/global';
import Card from '@airwallex/card';
import { Headline } from '@airwallex/typography';

import { useMobile } from '@/hooks/useMediaQuery';
import { useStore } from '@/providers/storeProvider';
import { event, Action, Category } from '@/utils/analytics/google';
import { useLanguage } from '@/hooks/useLanguage';
import validateEmail from '@/utils/validate-email';
import { MerchantInfo, Title, Description } from './description';
import Amount from './amount';
import InputFields from './inputFields';
import { formCss, submitCss, errorCss } from './styles';
import CURRENCIES from './currencies';
import { LinkInfoData, PayByLinkValues, Error, Option } from './types';
import DesktopOrderInfo from './orderSummary/desktopOrderInfo';

type Props = {
  linkInfo: LinkInfoData;
  error?: Error;
  onSubmit: (values: PayByLinkValues) => void;
  submitting?: boolean;
  initialValues: PayByLinkValues;
};

interface FormProps extends FormikProps<PayByLinkValues> {
  linkInfo: LinkInfoData;
  error?: Error;
  submitting?: boolean;
}
const PayByLinkForm: React.FC<FormProps> = ({
  linkInfo,
  values,
  setFieldValue,
  error,
  submitting,
  isValid,
  validateForm,
  // eslint-disable-next-line sonarjs/cognitive-complexity
}) => {
  const { t } = useTranslation();
  const isMobile = useMobile();
  const { intentData, shopperData } = useStore();
  const fixedCurrency = linkInfo?.minAmount?.ccy;
  const amount = values.amount && Number(values.amount.replace(/,/g, ''));
  const isValidAmount =
    (amount >= (linkInfo?.minAmount?.amount || 0) && amount > 0 && (values.currency || fixedCurrency)) ||
    linkInfo?.amount;
  const submitDisabled = !isValidAmount || !!submitting || !isValid;
  const language = useLanguage();
  useEffect(() => {
    validateForm();
  }, [t]);
  const currencies = useMemo(() => {
    const hasAllCurrencies = linkInfo.currencyList[0] === '*';
    const currencyList = hasAllCurrencies ? CURRENCIES : linkInfo.currencyList;
    return currencyList.map(
      (currency): Option => ({
        value: currency,
        name: currency,
        subLabel: currencyNames[currency as keyof typeof currencyNames]?.[language] || currency,
        subLabelEn: currencyNames[currency as keyof typeof currencyNames]?.en || currency,
        icon: <Flags size={28} currency={currency} />,
        showSubLabel: false,
      })
    );
  }, [linkInfo.currencyList, language]);

  useEffect(() => {
    if (!linkInfo?.amount && !linkInfo?.minAmount) {
      if (intentData?.amount?.ccy) {
        setFieldValue('currency', intentData.amount.ccy);
      } else if (linkInfo?.defaultCurrency) {
        setFieldValue('currency', linkInfo.defaultCurrency);
      } else {
        const includeUSD = currencies.find((currency) => currency.value === 'USD');
        setFieldValue('currency', includeUSD ? 'USD' : currencies[0].value);
      }
      if (intentData?.amount.amount) {
        setFieldValue('amount', String(intentData.amount.amount));
      }
    }
  }, [currencies, linkInfo, setFieldValue, intentData]);
  useEffect(() => {
    const fieldsToCheck = [
      'name',
      'email',
      'sendEmail',
      'phoneNumber',
      'reference',
      'shippingAddress',
      'comment',
    ] as const;
    fieldsToCheck.forEach((field) => {
      if (shopperData?.[field]) {
        setFieldValue(field, shopperData[field]);
      }
    });
    // validate the form a tick after setting their values
    setTimeout(() => {
      validateForm();
    }, 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const handleSubmitClick = () => {
    event(Category.ContinueToPayment, Action.Click);
  };
  const amountElement = (
    <Amount
      linkInfo={linkInfo}
      currencies={currencies}
      fixedCurrency={fixedCurrency}
      defaultAmount={intentData?.amount?.amount}
      defaultCurrency={intentData?.amount?.ccy}
    />
  );
  const merchantInfoElement = <MerchantInfo linkInfo={linkInfo} />;
  const titleElement = <Title linkInfo={linkInfo} />;
  const descriptionElement = <Description linkInfo={linkInfo} />;
  const formContent = (
    <Fragment>
      <InputFields linkInfo={linkInfo} />
      <Button
        variant="PRIMARY"
        css={submitCss(submitDisabled)}
        type="submit"
        disabled={submitDisabled}
        onClick={handleSubmitClick}
      >
        {t('pay_by_link.button.pay')}
      </Button>
      {error?.message && <p css={errorCss}>{error?.message}</p>}
    </Fragment>
  );

  return (
    <Form css={formCss} data-testid="form">
      {isMobile ? (
        <div
          css={css`
            margin-top: 72px;
          `}
        >
          {merchantInfoElement}
          {titleElement}
          {descriptionElement}
          {amountElement}
        </div>
      ) : (
        <div
          css={css`
            width: 345px;
          `}
        >
          <DesktopOrderInfo
            logoUrl={linkInfo?.logoUrl}
            merchant={linkInfo?.merchant}
            title={linkInfo?.title}
            description={linkInfo?.description}
          />
          {amountElement}
        </div>
      )}
      {isMobile ? (
        formContent
      ) : (
        <Card
          css={css`
            width: 605px;
            padding: ${space.xl};
            box-shadow: 0 0 16px 0 rgb(0 0 0 / 8%);
            border: 1px solid #ebecf0;
          `}
        >
          <Headline
            css={css`
              margin-bottom: ${space.m};
            `}
          >
            {t('pay_by_link.form.title')}
          </Headline>
          {formContent}
        </Card>
      )}
    </Form>
  );
};

const Index: FC<Props> = ({ onSubmit, linkInfo, error, submitting, initialValues }) => {
  const { t } = useTranslation();
  const handleValidate = (values: PayByLinkValues) => {
    // key: field name, value: error message. Empty when no error
    const errors: Partial<PayByLinkValues> = {};
    if (
      formatCurrency({
        amount: Number(values.amount.replace(/,/g, '')),
        currency: values.currency,
        showCode: false,
      }).length >= 14
    ) {
      errors.amount = t('pay_by_link.length_error');
    }
    const requiredShopperData = linkInfo?.requiredShopperData;
    if (requiredShopperData) {
      // required fields
      const requiredFields = ['name', 'email', 'phoneNumber', 'reference'] as const;
      requiredFields.forEach((field) => {
        if (requiredShopperData[field] && !values[field]?.trim()) {
          errors[field] = t('required');
        }
      });
      if (requiredShopperData.email && values.email && !validateEmail(values.email)) {
        errors.email = t('pay_by_link.form.email.invalid');
      }
      // shipping address check
      if (requiredShopperData.shippingAddress) {
        const addressFields = ['streetAddress', 'city', 'state', 'country', 'postCode'] as const;
        addressFields.forEach((field) => {
          if (!values.shippingAddress[field]?.trim()) {
            if (!errors.shippingAddress) {
              errors.shippingAddress = {
                streetAddress: '',
                city: '',
                state: '',
                country: '',
                postCode: '',
              };
            }
            errors.shippingAddress[field] = t('required');
          }
        });
      }
    }
    return errors;
  };
  return (
    <Formik
      initialValues={initialValues}
      onSubmit={onSubmit}
      validate={handleValidate}
      validateOnChange
      validateOnBlur={false}
    >
      {(props) => {
        return <PayByLinkForm {...props} linkInfo={linkInfo} error={error} submitting={submitting} />;
      }}
    </Formik>
  );
};

export default Index;
